import { currencyFormatter } from "@alba-cars/common-modules";
import { useQuery } from "@tanstack/react-query";
import { Car, Cog, Gauge, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { Control, useFormContext } from "react-hook-form";

import FormTextField from "@/components/common/forms/FormTextField";
import { useMultiStepFormSteps } from "@/contexts/MultiStepFormContext";
import { getBankValuationDealDriveEstimate } from "@/lib/api/deal-drive-estimate/get-deal-drive-estimate";
import { DealDriveEstimateRequest } from "@/lib/types";
import { convertDistance } from "@/lib/utils";
import {
  BankValuationCompleteFormSchemaType,
  BankValuationEstimatedPriceStepSchemaType,
} from "@/schemas/validation/bankValuationSchema";
import { numberAsPercentage } from "@/utils/utils";

interface BankValuationEstimatedPriceStepProps {
  control: Control<BankValuationEstimatedPriceStepSchemaType>;
}

const calculateToPercentage = (value: number) => {
  if (value >= 0 && value <= 50_000) {
    return numberAsPercentage(25);
  } else if (value > 50_000 && value <= 100_000) {
    return numberAsPercentage(20);
  } else if (value > 100_000 && value <= 150_000) {
    return numberAsPercentage(15);
  } else if (value > 150_000) {
    return numberAsPercentage(10);
  }

  return numberAsPercentage(20);
};

const getEngineCylinderCount = (
  engineType: "Electric" | "3-Cylinder" | "4-Cylinder" | "5-Cylinder" | "V6" | "V8" | "V10" | "W12",
) => {
  switch (engineType) {
    case "Electric":
      return undefined;
    case "3-Cylinder":
      return 3;
    case "4-Cylinder":
      return 4;
    case "5-Cylinder":
      return 5;
    case "V6":
      return 6;
    case "V8":
      return 8;
    case "V10":
      return 10;
    case "W12":
      return 12;
  }
};

const TEN_PERCENT = numberAsPercentage(10);

const calculateMileageInKms = (mileage: number, mileageUnit: "km" | "miles") => {
  if (mileageUnit === "km") {
    return mileage;
  }

  return convertDistance(mileage, { from: "miles", to: "km" });
};

const BankValuationEstimatedPriceStep = ({ control }: BankValuationEstimatedPriceStepProps) => {
  const { getValues, setValue, watch } = useFormContext<BankValuationCompleteFormSchemaType>();
  const formData = getValues();
  const t = useTranslations("BankValuation.estimatedPrice");

  const { goToStep } = useMultiStepFormSteps();

  const engineType = watch("engineType") as "Electric" | "4-Cylinder" | "5-Cylinder" | "V6" | "V8" | "V10" | "W12";

  const engineCylinderCount = getEngineCylinderCount(engineType);

  const trimOption = watch("trimOption");
  const mileageUnit = (watch("mileageUnit") ?? "km") as "km" | "miles";

  const dealDrivePayload: DealDriveEstimateRequest = {
    make: formData.make,
    makeName: formData.makeName,
    model: formData.model,
    modelName: formData.modelName,
    trim: formData.trim ?? "",
    specification: formData.specification,
    specificationName: formData.specs,
    year: Number.isNaN(Number(formData.year)) ? 0 : Number(formData.year),
    mileage: formData.mileage ? calculateMileageInKms(Number(formData.mileage), mileageUnit) : 0,
    engineSize: formData.engineSize ? Number(formData.engineSize) : undefined,
    ...(engineCylinderCount ? { engineCylinderCount } : {}),
    ...(trimOption ? { trimOption } : {}),
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["deal-drive-estimate", dealDrivePayload],
    queryFn: () => getBankValuationDealDriveEstimate(dealDrivePayload),
    refetchOnWindowFocus: false,
    enabled: !!dealDrivePayload,
  });

  const marketPrice = data?.data?.marketPrice ?? 0;
  const toPercentage = calculateToPercentage(marketPrice);
  const fromValue = marketPrice > 0 ? marketPrice - marketPrice * TEN_PERCENT : 0;
  const toValue = marketPrice > 0 ? marketPrice + marketPrice * toPercentage : 0;

  useEffect(() => {
    if (marketPrice) {
      setValue("dealDriveEstimatedPrice", marketPrice);
    }
  }, [marketPrice, setValue]);

  const noValuationFound = isError || data?.error || !data?.data.marketPrice;

  const formatCurrency = (value: number) => currencyFormatter({ isDecimal: true, decimalPlaces: 0 }).format(value);

  return (
    <>
      <div className="mt-4 flex flex-col items-center gap-2 rounded-2xl bg-gray-50 px-4 py-6">
        {!isLoading && noValuationFound ? (
          <span className="inter mb-4 text-center text-sm font-normal text-primary-foreground">
            {t("noValuationMessage")} <br /> <br /> {t("goBackAndChange")}{" "}
            <button className="text-primary underline" onClick={() => goToStep(1)}>
              {t("changeLink")}
            </button>
          </span>
        ) : null}
        {!noValuationFound && (
          <span className="inter text-center text-lg font-normal text-primary-foreground">
            {t("estimatedValueTitle")}
          </span>
        )}
        {isLoading ? (
          <span>
            <Loader2 size={16} className="animate-spin" />
          </span>
        ) : (
          <span className="urbanist text-base font-extrabold sxs:text-lg ixs:text-2xl md:text-4xl">
            {noValuationFound
              ? t("estimationNotAvailable")
              : `${formatCurrency(fromValue)} - ${formatCurrency(toValue)}`}
          </span>
        )}
        {!isLoading && noValuationFound ? (
          <span className="inter text-center text-xs font-normal text-secondary-foreground">
            {t("noValuationHint")}
          </span>
        ) : null}

        <hr className="my-1 w-full border-t-2 border-gray-200" />

        <div className="inter flex flex-col items-center justify-center gap-2">
          <span className="text-center text-sm font-semibold md:text-base">
            {formData.makeName} {formData.trimName ? formData.trimName : formData.modelName}{" "}
            {formData.trimOptionName ? formData.trimOptionName : ""} {formData.year}
          </span>
          <div className="flex flex-wrap justify-center gap-3">
            <span className="flex items-center gap-2 text-xs md:text-sm">
              <Car size={16} />
              {formData.engineType}
            </span>
            <span className="flex items-center gap-2 text-xs md:text-sm">
              <Gauge size={16} />
              {formData.mileage.toLocaleString()} {mileageUnit === "km" ? t("kms") : t("miles")}
            </span>
            <span className="flex items-center gap-2 text-xs md:text-sm">
              <Cog size={16} strokeWidth={1} />
              {formData.specs}
            </span>
          </div>
        </div>
      </div>
      <div className="mb-8 mt-6 grid grid-cols-1 gap-4">
        <FormTextField
          name="value"
          control={control}
          placeholder={noValuationFound ? t("inputValuePlaceholderNoEstimate") : t("inputValuePlaceholder")}
          required
          containerClassName=""
          label={noValuationFound ? t("inputValueLabelNoEstimate") : t("inputValueLabel")}
          formatNumber
          type="number"
          useCustomValidation
          validate={(value) => {
            const toValueRounded = Math.round(toValue);
            const fromValueRounded = Math.round(fromValue);

            if (!toValueRounded || !fromValueRounded) {
              return true;
            }

            const n = Number(value);
            if (Number.isNaN(n)) {
              return t("validationError");
            }

            if (n > toValueRounded || n < fromValueRounded) {
              return t("rangeValidationError", {
                fromValue: formatCurrency(fromValue),
                toValue: formatCurrency(toValue),
              });
            }

            return true;
          }}
        />
      </div>
    </>
  );
};

export default BankValuationEstimatedPriceStep;
